import EmailIcon from "@mui/icons-material/Email";
import useExcelUrl from "app/useExcelUrl";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridModalEditColumn,
  GridColumns,
  PageHeader,
} from "common";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { EmailTemplateItem } from "./emailTemplateApi";
import EmailTemplateForm from "./EmailTemplateForm";
import useEmailTemplateList from "./useEmailTemplateList";

export interface EmailTemplateListProps extends Omit<
  DataGridBaseBaseProps,
  "id"
> {
  showHeader?: boolean;
}

export default function EmailTemplateList({
  showHeader = true,
  ...props
}: EmailTemplateListProps) {
  const { params, scope, editUrl, ...service } = useEmailTemplateList();
  const { hasPermissions, canEditItem } = useAccess();
  const excelUrl = useExcelUrl("email-templates");

  let columns: GridColumns<EmailTemplateItem> = [
    {
      field: "name",
      headerName: I18n.t("EmailTemplate.attributes.name"),
      renderCell: params => (
        <DataGridModalEditColumn
          href={`${editUrl}${params.row?.id}`}
          params={params}
        />
      ),
      flex: 1,
    },
    {
      field: "subject",
      headerName: I18n.t("EmailTemplate.attributes.subject"),
      flex: 1,
    },
    {
      field: "description",
      headerName: I18n.t("EmailTemplate.attributes.description"),
      flex: 1,
    },
    {
      field: "is_active",
      headerName: I18n.t("EmailTemplate.attributes.is_active"),
      type: "boolean",
    },
    {
      field: "created_at",
      headerName: I18n.t("App.created_at"),
      type: "datetime",
      hideable: true,
    },
    {
      field: "updated_at",
      headerName: I18n.t("App.updated_at"),
      type: "datetime",
      hideable: true,
    },
  ];

  return (
    <>
      {showHeader && (
        <PageHeader
          avatar={<EmailIcon />}
          title={I18n.t("EmailTemplate.title")}
        />
      )}
      <DataGrid
        {...service}
        columns={columns}
        id="email-templates"
        excelUrl={excelUrl}
        renderModalForm={props => <EmailTemplateForm {...props} />}
        modalProps={{ maxWidth: "md" }}
        {...props}
        canCreate={hasPermissions("email_template.create")}
        canDelete={hasPermissions([
          "email_template.editany",
          "email_template.editown",
        ])}
        canEdit={hasPermissions([
          "email_template.editany",
          "email_template.editown",
        ])}
        canDeleteRow={params => canEditItem("email_template", params.row)}
        canEditRow={params => canEditItem("email_template", params.row)}
        canBatchActions={hasPermissions([
          "email_template.editany",
          "email_template.editown",
        ])}
        canHandleTrash={hasPermissions([
          "email_template.editany",
          "email_template.editown",
        ])}
      />
    </>
  );
}
